import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Logo } from "@/components/cinch/Logo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { features } from "@/features/registry";
import { NAV_GROUP_ORDER, type FeatureNav, type NavGroup } from "@/features/types";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "cinch.sidebar.collapsed";
const OTHER_GROUP = "Other" as const;
type GroupKey = NavGroup | typeof OTHER_GROUP;

// Each content group gets a signature icon hue so the rail reads as color-coded
// zones rather than one gray list. Labels stay neutral — only the icon carries
// the color. Adjacent groups are spaced around the wheel so neighbors never
// blur together, and a couple of hues earn their meaning (Revenue=green money,
// Monitors=orange alert). Finance takes the info blue between green and purple. The bottom utility zone (Tools, System) stays muted
// gray on purpose, which makes the colored groups read louder. All pulled from
// the design tokens, no hex literals — every one clears 3:1 on the white rail
// (the amber `warning` token would not, so it's deliberately skipped). Active
// rows override this with power-blue below, so the color lives on resting +
// hover icons only.
const GROUP_ICON_COLOR: Record<GroupKey, string> = {
  Overview: "text-brand",
  Revenue: "text-success",
  Finance: "text-info",
  Operations: "text-purple",
  Monitors: "text-orange",
  Strategy: "text-teal",
  Tools: "text-muted-foreground",
  System: "text-muted-foreground",
  [OTHER_GROUP]: "text-muted-foreground",
};

function useCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    // Expose the current sidebar width as a CSS var so other layout pieces
    // (e.g. an expanded EntityDrawer) can size against it without prop drilling.
    document.documentElement.style.setProperty(
      "--sidebar-w",
      collapsed ? "3.5rem" : "15rem",
    );
  }, [collapsed]);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return [collapsed, toggle];
}

interface SidebarItem {
  key: string;
  nav: FeatureNav;
}

export function Sidebar() {
  const router = useRouterState();
  const current = router.location.pathname;
  const [collapsed, toggle] = useCollapsed();

  // Build the visible nav from the feature registry, filtered by permission,
  // then bucket by group while preserving order-within-group.
  const allowed = features.map((f) => ({
    key: f.key,
    nav: f.nav,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    allowed: useCan(f.nav.requires),
  }));

  const grouped = useMemo(() => {
    const buckets = new Map<GroupKey, SidebarItem[]>();
    for (const item of allowed) {
      if (!item.allowed) continue;
      const g: GroupKey = item.nav.group ?? OTHER_GROUP;
      const arr = buckets.get(g) ?? [];
      arr.push({ key: item.key, nav: item.nav });
      buckets.set(g, arr);
    }
    for (const arr of buckets.values()) {
      arr.sort((a, b) => a.nav.order - b.nav.order);
    }
    const ordered: { group: GroupKey; items: SidebarItem[] }[] = [];
    for (const g of NAV_GROUP_ORDER) {
      const items = buckets.get(g);
      if (items?.length) ordered.push({ group: g, items });
    }
    const other = buckets.get(OTHER_GROUP);
    if (other?.length) ordered.push({ group: OTHER_GROUP, items: other });
    return ordered;
  }, [allowed]);

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          "hidden shrink-0 border-r border-border bg-card md:flex md:flex-col transition-[width] duration-200",
          collapsed ? "w-14" : "w-60",
        )}
      >
        <div
          className={cn(
            "flex items-center border-b border-border",
            collapsed ? "h-14 justify-center px-2" : "h-14 justify-between px-4",
          )}
        >
          {collapsed ? (
            <Logo variant="mark" className="h-10 w-10" />
          ) : (
            <Logo variant="wordmark" tone="brand" className="h-6 w-auto" />
          )}
          {!collapsed ? (
            <button
              type="button"
              onClick={toggle}
              aria-label="Collapse sidebar"
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {collapsed ? (
          <div className="border-b border-border p-1">
            <button
              type="button"
              onClick={toggle}
              aria-label="Expand sidebar"
              className="flex h-8 w-full items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto py-3">
          {grouped.map(({ group, items }) => (
            <div key={group}>
              {!collapsed ? (
                <div className="mb-1 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </div>
              ) : null}
              <ul className={cn("flex flex-col", collapsed ? "gap-0.5 px-1" : "gap-0.5 px-2")}>
                {items.map(({ key, nav }) => {
                  const Icon = nav.icon;
                  const active =
                    current === nav.to || current.startsWith(nav.to + "/");
                  const row = (
                    <Link
                      to={nav.to}
                      className={cn(
                        "flex items-center rounded-md text-sm font-medium transition-colors",
                        collapsed ? "h-9 w-full justify-center" : "gap-2.5 px-3 py-2",
                        active
                          ? "bg-power-light text-power"
                          : "text-foreground/80 hover:bg-accent hover:text-foreground",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          !active && GROUP_ICON_COLOR[group],
                        )}
                      />
                      {!collapsed ? <span className="truncate">{nav.label}</span> : null}
                    </Link>
                  );
                  if (!collapsed) return <li key={key}>{row}</li>;
                  return (
                    <li key={key}>
                      <Tooltip>
                        <TooltipTrigger asChild>{row}</TooltipTrigger>
                        <TooltipContent side="right">{nav.label}</TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </TooltipProvider>
  );
}
